// src/components/lesson/PlacementVerdictView.tsx
//
// The verdict on a "Prove I know this" placement check — shown on the practice
// runner's results screen and, when an unfinished check is scored on restart,
// in the /learn dialog. Pass: what opened and where to go next. Miss: what it
// showed, and the lesson / skill check instead. No penalty either way.
//
// Every link here is a FRESH navigation (FreshLink): grading just changed the
// student's path, and a plain <Link> could land on a page the client Router
// Cache still holds from before the check — /learn reading "0 passed" with the
// next step locked until a manual reload.

'use client'

import Link from 'next/link'
import { useRouter } from 'next/navigation'
import type { ComponentProps, MouseEvent } from 'react'
import { ArrowRight, BadgeCheck, BookOpen, Flag, Info } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { placementVerdictCopy, type PlacementVerdict } from '@/lib/learn/placement-check'

/**
 * A Link that re-fetches its destination: push, THEN refresh — router.refresh()
 * acts on the current route, so refreshing first would spend it on the page
 * being left (see goBack in src/app/practice/session/page.tsx). Modified clicks
 * (new tab, etc.) are left to the browser.
 */
export function FreshLink({
  href,
  onClick,
  ...rest
}: ComponentProps<typeof Link> & { href: string }) {
  const router = useRouter()
  const go = (e: MouseEvent<HTMLAnchorElement>) => {
    onClick?.(e)
    if (e.defaultPrevented || e.button !== 0 || e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) {
      return
    }
    e.preventDefault()
    router.push(href)
    router.refresh()
  }
  return <Link href={href} onClick={go} {...rest} />
}

export default function PlacementVerdictView({ verdict }: { verdict: PlacementVerdict }) {
  const { title, body } = placementVerdictCopy(verdict)
  const won = verdict.passed && verdict.stepPassed
  const tone = won
    ? 'border-emerald-500/40 bg-emerald-500/10'
    : verdict.void
      ? 'border-border bg-card/40'
      : 'border-amber-500/40 bg-amber-500/10'
  const Icon = won ? BadgeCheck : Info

  return (
    <section
      className={`rounded-lg border p-4 ${tone}`}
      data-testid="placement-verdict"
      data-passed={won ? 'true' : 'false'}
      aria-live="polite"
    >
      <h2 className="flex items-center gap-2 text-base font-semibold text-foreground">
        <Icon
          className={`h-5 w-5 shrink-0 ${won ? 'text-emerald-600 dark:text-emerald-400' : 'text-muted-foreground'}`}
          aria-hidden="true"
        />
        {title}
      </h2>
      <p className="mt-1 text-sm text-muted-foreground">{body}</p>
      <div className="mt-3 flex flex-wrap gap-2">
        {won ? (
          verdict.next ? (
            <Button asChild size="sm" className="bg-brand text-on-brand hover:bg-brand-hover">
              <FreshLink href={`/learn/${verdict.next.lessonSlug}`}>
                Next: {verdict.next.topic}
                <ArrowRight className="ml-1.5 h-3.5 w-3.5" aria-hidden="true" />
              </FreshLink>
            </Button>
          ) : (
            <Button asChild size="sm" className="bg-brand text-on-brand hover:bg-brand-hover">
              <FreshLink href="/learn">
                Back to the path
                <ArrowRight className="ml-1.5 h-3.5 w-3.5" aria-hidden="true" />
              </FreshLink>
            </Button>
          )
        ) : (
          <>
            {verdict.lessonSlug && (
              <Button asChild size="sm" className="bg-brand text-on-brand hover:bg-brand-hover">
                <FreshLink href={`/learn/${verdict.lessonSlug}`}>
                  <BookOpen className="mr-1.5 h-3.5 w-3.5" aria-hidden="true" />
                  Read the lesson
                </FreshLink>
              </Button>
            )}
            <Button asChild size="sm" variant="outline">
              <FreshLink href={verdict.skillCheckHref}>
                <Flag className="mr-1.5 h-3.5 w-3.5" aria-hidden="true" />
                Take the skill check
              </FreshLink>
            </Button>
          </>
        )}
      </div>
    </section>
  )
}
